import { getElement, SUBJECT_YEAR_EDITING_ENABLED } from "./generics";
import { api } from "./api";
import { Subject } from "./subjects";
import { renderEditableLinkSection, EditableItem, buildTeachersEditor } from "./subjectTeacherLinks";

// Widok "wybierz rok" dla wydziałów z Faculty.subject_years_enabled=True (patrz models.py) -
// zastępuje na tych wydziałach dzisiejszy płaski, oceniany katalog (subjectBrowser.ts, dalej
// używany przez MIMUW) - to CELOWO osobny moduł od oceniania przedmiotów obieralnych (patrz
// plan): zwijane sekcje I-VI (lub mniej, patrz Faculty.max_study_years) rok, w każdej lista
// przedmiotów tego roku - też zwijana, pojedynczy przedmiot rozwija się do swojej listy
// prowadzących (edytowalnej na miejscu, ten sam komponent co miał się pojawiać na osobnej
// stronie przedmiotu - patrz buildTeachersEditor w subjectTeacherLinks.ts). Przedmioty na tych
// wydziałach NIE MAJĄ własnej strony (na wyraźną prośbę - wszystko dzieje się tutaj, w jednym
// miejscu) - w odróżnieniu od MIMUW, gdzie subject_detail.html wciąż istnieje (ocena/opinie).
// Dodawanie/usuwanie PRZEDMIOTU z danego roku (nie prowadzącego) chowa się za osobnym "Edytuj"
// na poziomie całej sekcji roku.
const info = getElement<HTMLDivElement>("subject-year-directory-info");
const statusBar = getElement<HTMLParagraphElement>("status-bar");
const list = getElement<HTMLDivElement>("year-directory-list");

const universitySlug = info.dataset.universitySlug || "";
const facultySlug = info.dataset.facultySlug || "";
const facultyId = Number(info.dataset.facultyId);
// Patrz Faculty.max_study_years w models.py - np. WPiA (jednolite magisterskie) ma 5, nie
// domyślne 6, więc "VI rok" tam w ogóle się nie pojawia.
const maxStudyYears = Number(info.dataset.maxStudyYears) || 6;

// Wiersz przedmiotu w liście roku - zwijany (<details>), rozwinięcie pokazuje edytowalną na
// miejscu listę prowadzących (klikalne linki do ich profili - to dokładnie to, co
// buildTeachersEditor/renderPlainChip już robi, więc nic dodatkowego nie trzeba tu dorabiać).
// Przedmioty nie mają tu własnej strony (patrz notka w views.py przy subject_detail) - żadnego
// linku poza tym. removeButton (usuń przedmiot z TEGO roku, nie prowadzącego) ląduje OBOK
// <details>, nie w jego środku - inaczej byłby niewidoczny po zwinięciu.
function renderSubjectDetails(
    item: EditableItem, removeButton: HTMLElement | null, subject: Subject, isElective: boolean,
): HTMLElement {
    const wrapper = document.createElement("div");
    wrapper.className = "year-subject-row";

    const details = document.createElement("details");
    details.className = "year-subject-details";

    const summary = document.createElement("summary");
    const summaryText = document.createElement("span");
    summaryText.className = "year-summary-text";
    summaryText.textContent = item.label;
    if (isElective) {
        const badge = document.createElement("span");
        badge.className = "subject-category-badge year-subject-elective-badge";
        badge.textContent = "obieralny";
        summaryText.appendChild(badge);
    }
    const teachersEditor = buildTeachersEditor(subject.id, subject.name, universitySlug, facultySlug, subject.teachers);
    // "Edytuj" (prowadzących) na tej samej linii co nazwa przedmiotu, wyjustowane do prawej
    // (<summary> sam jest kontenerem flex - patrz .year-subject-details summary w style.css,
    // .year-summary-text tam dostaje flex:1 i dociska resztę w prawo) - na wyraźną prośbę,
    // zamiast osobnej linii dopiero po rozwinięciu.
    summary.appendChild(summaryText);
    summary.appendChild(teachersEditor.editButton);
    details.appendChild(summary);

    const body = document.createElement("div");
    body.className = "year-subject-details-body";
    body.appendChild(teachersEditor.element);

    details.appendChild(body);
    wrapper.appendChild(details);
    if (removeButton) wrapper.appendChild(removeButton);

    return wrapper;
}

function buildYearSection(
    yearLabel: string,
    // year=null - "bez przypisanego roku" (patrz wywołanie niżej) - subjectsInYear w tym
    // przypadku nie ma ŻADNEGO study_years wpisu, więc nie ma czego szukać po numerze roku.
    year: number | null,
    subjectsInYear: Subject[],
    allSubjects: Subject[],
): HTMLElement {
    const details = document.createElement("details");
    details.className = "year-directory-section";

    const summary = document.createElement("summary");
    const summaryText = document.createElement("span");
    summaryText.className = "year-summary-text";
    summaryText.textContent = `${yearLabel} (${subjectsInYear.length})`;
    summary.appendChild(summaryText);
    details.appendChild(summary);

    const subjectsById = new Map(allSubjects.map(s => [s.id, s]));

    // linkId MUSI wskazywać na wiersz SubjectStudyYear dla TEGO KONKRETNEGO roku - przedmiot
    // bywa przypisany do kilku lat naraz (np. "Doktryny polityczno-prawne" - III/IV/V rok), więc
    // branie pierwszego wpisu study_years byłoby przypadkowe/błędne poza pierwszym rokiem, w
    // którym się pojawia. electiveBySubjectId - ten sam powód (is_elective jest per-rok, nie
    // per-przedmiot, patrz SubjectStudyYear.is_elective w models.py).
    const electiveBySubjectId = new Map<number, boolean>();
    const items: EditableItem[] = subjectsInYear.map(s => {
        const yearLink = year !== null ? s.study_years.find(y => y.year_number === year) : undefined;
        electiveBySubjectId.set(s.id, yearLink?.is_elective ?? false);
        return { linkId: yearLink?.id ?? s.id, entityId: s.id, label: s.name, href: "" };
    });

    const body = document.createElement("div");
    body.className = "year-directory-section-body";

    if (year !== null && SUBJECT_YEAR_EDITING_ENABLED) {
        const subjectsEditor = renderEditableLinkSection({
            heading: "",
            initialItems: items,
            emptyHint: "Żaden przedmiot nie jest jeszcze przypisany do tego roku.",
            listClassName: "year-subject-list",
            renderContent: (item, removeButton) =>
                renderSubjectDetails(item, removeButton, subjectsById.get(item.entityId)!, electiveBySubjectId.get(item.entityId) ?? false),
            confirmRemoveMessage: (item) => `Czy na pewno ${item.label} nie jest już prowadzone na ${year} roku?`,
            onRemove: async (item) => (await api.removeSubjectStudyYear(item.linkId)).ok,
            searchPlaceholder: "Dodaj przedmiot do tego roku...",
            loadCandidates: async () => allSubjects.map(s => ({ id: s.id, label: s.name })),
            onPick: async (candidate) => {
                const addRes = await api.addSubjectStudyYear(candidate.id, year);
                if (!addRes.ok) return addRes.error;
                return { linkId: addRes.data.id, entityId: candidate.id, label: candidate.label, href: "" };
            },
            // "Edytuj" na tej samej linii co "N rok (M)", wyjustowane do prawej (<summary> sam
            // jest kontenerem flex - patrz .year-directory-section summary w style.css, heading
            // tu jest celowo puste) - zamiast osobnej, wizualnie "pustej" linii pod spodem.
            externalEditButton: true,
        });
        summary.appendChild(subjectsEditor.editButton);
        body.appendChild(subjectsEditor.element);
    } else {
        // Tylko do odczytu na TYM poziomie - albo "bez przypisanego roku" (nie ma sensownej akcji
        // "dodaj przedmiot BEZ roku" - to stan wynikający z braku przypisania, nie coś, co się
        // jawnie "dodaje"), albo edycja przypisania przedmiotów do roku jest tymczasowo schowana
        // (patrz SUBJECT_YEAR_EDITING_ENABLED w generics.ts, WYLACZONE_FUNKCJE.md) - prowadzący
        // każdego przedmiotu poniżej są mimo to edytowalni w OBU przypadkach, ten sam komponent
        // co wyżej (buildTeachersEditor w renderSubjectDetails, celowo osobna flaga/nietknięta).
        const listEl = document.createElement("div");
        listEl.className = "year-subject-list";
        items.forEach(item => listEl.appendChild(renderSubjectDetails(item, null, subjectsById.get(item.entityId)!, false)));
        body.appendChild(listEl);
    }

    details.appendChild(body);
    return details;
}

async function load(): Promise<void> {
    statusBar.textContent = "Ładowanie...";
    list.replaceChildren();

    const res = await api.getSubjectsCatalog(facultyId);
    if (!res.ok) {
        statusBar.textContent = `Błąd: ${res.error}`;
        return;
    }
    statusBar.textContent = "";

    const subjects = res.data.subjects;

    if (subjects.length === 0) {
        const empty = document.createElement("div");
        empty.className = "empty-faculty-state";
        const heading = document.createElement("p");
        heading.className = "empty-faculty-heading";
        heading.textContent = "Nikt jeszcze nie dodał tu żadnego przedmiotu.";
        empty.appendChild(heading);
        list.appendChild(empty);
        return;
    }

    for (let year = 1; year <= maxStudyYears; year++) {
        const subjectsInYear = subjects.filter(s => s.study_years.some(y => y.year_number === year));
        list.appendChild(buildYearSection(`${year} rok`, year, subjectsInYear, subjects));
    }

    // Sekcja "Bez przypisanego roku (obieralne)" USUNIĘTA z tej strony na wyraźną prośbę (były to
    // realne przedmioty obieralne z katalogu USOS, nie śmieci jak BHP - patrz TODO.md/pamięć
    // projektowa - więc rekordy Subject ZOSTAJĄ w bazie, tylko nie renderujemy już tego kubełka
    // tutaj). Wciąż da się je przypisać do konkretnego roku - wyszukiwarka "Dodaj przedmiot do
    // tego roku" w każdej sekcji roku (patrz buildYearSection -> loadCandidates) przeszukuje
    // WSZYSTKIE przedmioty wydziału, nie tylko już przypisane do jakiegoś roku.
}

load();
